import { useEffect, useMemo, useState, Fragment } from "react";
import api from "@/lib/api";
import { Loader2, Search, Download, ChevronLeft, ChevronRight, Activity, FileOutput, FolderOpen } from "lucide-react";

const SEVC = { high: "bg-red-500/15 text-red-400", medium: "bg-amber-500/15 text-amber-400", low: "bg-slate-500/15 text-slate-400" };
const money = (v) => `$${Number(v || 0).toLocaleString()}`;
const PAGE = 15;

const cellVal = (row, key) => {
  const v = row[key];
  if (v === null || v === undefined) return "—";
  if (["pipeline_value", "fee", "billed", "balance"].includes(key)) return money(v);
  if (key === "overall") return `${v}%`;
  return String(v);
};

export const Workspace = ({ wsKey, onBack, onOpenCase, onOpenMedical, onOpenExport }) => {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(1);
  const [page, setPage] = useState(0);
  const [stage, setStage] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setData(null); setQ(""); setSortKey(null); setPage(0); setStage(null); setExpanded(null);
    api.get(`/firm/command/workspace/${wsKey}`).then(({ data }) => setData(data)).catch(() => setData({ title: "Error", columns: [], rows: [] }));
  }, [wsKey]);

  const rows = useMemo(() => {
    if (!data) return [];
    let r = data.rows || [];
    if (stage) r = r.filter((x) => (x.stage || x.status) === stage);
    if (q.trim()) {
      const ql = q.toLowerCase();
      r = r.filter((x) => JSON.stringify(x).toLowerCase().includes(ql));
    }
    if (sortKey) {
      r = [...r].sort((a, b) => {
        const av = a[sortKey], bv = b[sortKey];
        if (typeof av === "number" && typeof bv === "number") return (av - bv) * sortDir;
        return String(av ?? "").localeCompare(String(bv ?? "")) * sortDir;
      });
    }
    return r;
  }, [data, q, sortKey, sortDir, stage]);

  const exportCsv = () => {
    const cols = data.columns.map(([k]) => k);
    const csv = [data.columns.map(([, l]) => l).join(","),
      ...rows.map((r) => cols.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${wsKey}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
  };

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-blue-500 animate-spin" /></div>;
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  const view = rows.slice(page * PAGE, (page + 1) * PAGE);
  const rowOpen = (r) => (r.open === "medical" && r.case_id ? onOpenMedical(r) : r.case_id ? onOpenCase(r) : null);

  return (
    <div data-testid={`workspace-${wsKey}`}>
      <div className="flex items-center gap-2 text-[11px] text-slate-500 mb-3" data-testid="workspace-breadcrumb">
        <button onClick={onBack} data-testid="breadcrumb-dashboard" className="hover:text-blue-400 font-semibold">Dashboard</button>
        <span>/</span><span className="text-slate-300 font-semibold">{data.title}</span>
        <span className="ml-auto text-slate-500">{rows.length} record(s)</span>
      </div>

      {data.breakdown && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-4" data-testid="revenue-breakdown">
          {data.breakdown.map((b, i) => (
            <div key={i} className="bg-[#0E1624] rounded-2xl border border-[#1E2C45] p-4">
              <div className="text-xl font-extrabold text-white font-heading">{money(b.value)}</div>
              <div className="mt-1 text-[10px] uppercase tracking-wide text-slate-400 font-semibold">{b.label}</div>
              <div className="mt-1 text-[9px] text-slate-600">{b.formula}</div>
            </div>
          ))}
        </div>
      )}
      {data.stages && (
        <div className="flex flex-wrap gap-1.5 mb-4" data-testid="workspace-stages">
          {data.stages.map((s) => (
            <button key={s.stage} onClick={() => { setStage(stage === s.stage ? null : s.stage); setPage(0); }}
              data-testid={`stage-${s.stage.replace(/\W/g, "")}`}
              className={`px-3 py-1.5 rounded-full text-[10px] font-bold border transition-all ${stage === s.stage ? "bg-blue-600 border-blue-500 text-white" : s.count ? "bg-[#0E1624] border-[#1E2C45] text-slate-300" : "bg-transparent border-[#152033] text-slate-600"}`}>
              {s.stage} · {s.count}{s.value !== undefined ? ` · ${money(s.value)}` : ""}
            </button>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex items-center gap-2 bg-[#0E1624] border border-[#1E2C45] rounded-full px-3 py-1.5 flex-1 min-w-[200px] max-w-sm">
          <Search className="w-3.5 h-3.5 text-slate-500" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} data-testid="workspace-search"
            placeholder="Search this workspace…" className="flex-1 bg-transparent text-xs text-slate-200 outline-none placeholder:text-slate-600" />
        </div>
        <button onClick={exportCsv} data-testid="workspace-export" className="flex items-center gap-1.5 px-3.5 py-1.5 rounded-full bg-[#0E1624] border border-[#1E2C45] text-slate-300 text-xs font-semibold hover:border-blue-500/40">
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>

      <div className="bg-[#0E1624] rounded-2xl border border-[#1E2C45] overflow-x-auto">
        <table className="w-full text-left text-[11px]">
          <thead>
            <tr className="border-b border-[#1E2C45]">
              {data.columns.map(([k, l]) => (
                <th key={k} className="px-3 py-2.5">
                  <button onClick={() => { setSortDir(sortKey === k ? -sortDir : 1); setSortKey(k); }} data-testid={`sort-${k}`}
                    className="text-[9px] uppercase tracking-[0.15em] text-slate-500 font-bold hover:text-blue-400">
                    {l}{sortKey === k ? (sortDir === 1 ? " ↑" : " ↓") : ""}
                  </button>
                </th>
              ))}
              <th className="px-3 py-2.5 text-[9px] uppercase tracking-[0.15em] text-slate-500 font-bold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {view.map((r, i) => (
              <Fragment key={i}>
                <tr onClick={() => (r.missing ? setExpanded(expanded === i ? null : i) : rowOpen(r))}
                  data-testid={`ws-row-${i}`}
                  className="border-b border-[#152033] last:border-0 hover:bg-[#152033]/60 cursor-pointer transition-colors">
                  {data.columns.map(([k]) => (
                    <td key={k} className="px-3 py-2.5 text-slate-300 max-w-[260px] truncate">
                      {k === "severity" && SEVC[r[k]] ? <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${SEVC[r[k]]}`}>{r[k]}</span> : cellVal(r, k)}
                    </td>
                  ))}
                  <td className="px-3 py-2">
                    <span className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                      {(r.actions || []).includes("case") && r.case_id && (
                        <button onClick={() => onOpenCase(r)} title="Open case file" data-testid={`ws-open-case-${i}`}
                          className="p-1.5 rounded-full bg-[#152033] text-slate-400 hover:text-blue-400"><FolderOpen className="w-3.5 h-3.5" /></button>)}
                      {(r.actions || []).includes("medical") && r.case_id && (
                        <button onClick={() => onOpenMedical(r)} title="Medical Command" data-testid={`ws-open-medical-${i}`}
                          className="p-1.5 rounded-full bg-[#152033] text-slate-400 hover:text-emerald-400"><Activity className="w-3.5 h-3.5" /></button>)}
                      {(r.actions || []).includes("export") && r.case_id && (
                        <button onClick={() => onOpenExport(r)} title="Generate demand / exports" data-testid={`ws-open-export-${i}`}
                          className="p-1.5 rounded-full bg-[#152033] text-slate-400 hover:text-blue-400"><FileOutput className="w-3.5 h-3.5" /></button>)}
                    </span>
                  </td>
                </tr>
                {expanded === i && r.missing && (
                  <tr className="border-b border-[#152033]" data-testid={`ws-detail-${i}`}>
                    <td colSpan={data.columns.length + 1} className="px-4 py-3 bg-[#0A111D]">
                      <div className="grid sm:grid-cols-2 gap-3 text-[11px]">
                        <div>{(r.done || []).map((x, j) => <div key={j} className="text-emerald-400/90">✔ {x}</div>)}</div>
                        <div>{(r.missing || []).map((x, j) => <div key={j} className="text-amber-400/90">⚠ {x}</div>)}
                          {(r.missing || []).length === 0 && <div className="text-emerald-400">Nothing outstanding</div>}</div>
                      </div>
                      <div className="mt-2 flex gap-2">
                        <button onClick={() => onOpenCase(r)} className="px-3 py-1 rounded-full bg-[#152033] text-slate-300 text-[10px] font-bold">Open Case File</button>
                        <button onClick={() => onOpenMedical(r)} className="px-3 py-1 rounded-full bg-emerald-600 text-white text-[10px] font-bold">Medical Command</button>
                        <button onClick={() => onOpenExport(r)} className="px-3 py-1 rounded-full bg-blue-600 text-white text-[10px] font-bold">Generate Demand / Export</button>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
            {view.length === 0 && (
              <tr><td colSpan={data.columns.length + 1} className="px-4 py-8 text-center text-xs text-slate-500" data-testid="workspace-empty">
                {data.empty_hint || "No records match this metric right now."}</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="mt-3 flex items-center justify-end gap-2 text-[11px] text-slate-400" data-testid="workspace-pagination">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0} data-testid="page-prev"
            className="p-1.5 rounded-full bg-[#0E1624] border border-[#1E2C45] disabled:opacity-30"><ChevronLeft className="w-3.5 h-3.5" /></button>
          <span>Page {page + 1} / {pages}</span>
          <button onClick={() => setPage(Math.min(pages - 1, page + 1))} disabled={page >= pages - 1} data-testid="page-next"
            className="p-1.5 rounded-full bg-[#0E1624] border border-[#1E2C45] disabled:opacity-30"><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
};

export default Workspace;
